import React from 'react';
import { Link } from 'react-router-dom';
import { usePageLastUpdated } from '../../hooks/usePageLastUpdated';
import './ProjectPage.css';

const PAGE_LAST_UPDATED = { dateTime: '2010-05-26', displayDate: 'May 26, 2010' };

const NavLinks = () => (
  <p className="text-small text-italic text-gray">
    <a href="#top">Go to top</a> | <a href="#contents">Go to contents</a>
  </p>
);

const YtCrawlerRelease1Page = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);

  const standardFeeds = [
    { name: 'Top rated', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/top_rated', desc: 'Most highly rated YouTube videos.' },
    { name: 'Top favorites', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/top_favorites', desc: 'Videos most frequently flagged as favorite.' },
    { name: 'Most viewed', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/most_viewed', desc: 'Most frequently watched YouTube videos.' },
    { name: 'Most popular', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/most_popular', desc: 'Most popular YouTube videos.' },
    { name: 'Most recent', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/most_recent', desc: 'Videos most recently submitted to YouTube.' },
    { name: 'Most discussed', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/most_discussed', desc: 'Videos that have received the most comments.' },
    { name: 'Most responded', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/most_responded', desc: 'Videos that receive the most video responses.' },
    { name: 'Recently featured', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/recently_featured', desc: 'Videos recently featured on the YouTube home page.' },
    { name: 'Videos for mobile phones', url: 'http://gdata.youtube.com/feeds/api/standardfeeds/watch_on_mobile', desc: 'Videos suitable for playback on mobile devices.' },
  ];

  return (
    <div className="project-page" id="top">
      <h1 className="title">YTCrawler Release 1</h1>
      <p>
        The YouTube Crawler is a project in progress and the software on this page is pre-release and subject to change.
        There are no guarantees for this software and you can use it on your own risk. The YouTube Crawler is free
        software and is licensed under the terms of{' '}
        <a href="http://www.gnu.org/licenses/gpl.html" target="_blank" rel="noopener noreferrer">
          GNU General Public License
        </a>
        .
      </p>

      <h1 id="contents">Contents</h1>
      <ol type="I">
        <li><a href="#ovewview">Architecture overview</a></li>
        <li><a href="#dbcluster">Database clustering</a></li>
        <li><a href="#usage">Using the crawler</a></li>
        <li><a href="#feeds">Understanding video feeds</a></li>
        <li><a href="#setup">Setup guide</a></li>
        <li><a href="#code">Code information</a></li>
        <li><a href="#dload">Download software</a></li>
      </ol>
      <NavLinks />

      <h1 id="ovewview">I. Architecture Overview</h1>
      <p>
        The YouTube Crawler is a software application that can be used to crawl video information from{' '}
        <a href="http://www.youtube.com/" target="_blank" rel="noopener noreferrer">
          YouTube
        </a>
        . The crawler can capture both <em>static information</em> such as videos' title, author, duration, publishing
        date, etc, as well as <em>dynamic information</em> such as videos' view count, number of comments, rating, etc.
        Because static and dynamic video information is usually recorded in a different manner (is sufficient to crawl
        static data only once, whereas dynamic data should be crawled periodically), the YouTube Crawler has three
        components.
      </p>
      <ul>
        <li><a href="#a1">A master crawler</a></li>
        <li><a href="#a2">A slave crawler</a></li>
        <li><a href="#a3">A data processing agent (under development)</a></li>
      </ul>
      <p>The crawler uses an Oracle database to store the crawled video information.</p>
      <p>
        <img src="/projects/ytcrawler/arch.png" width={464} height={373} alt="YouTube Crawler architecture" />
      </p>

      <h2 id="a1">The Master Crawler</h2>
      <p>
        The <em>master</em> is responsible for initial crawling of YouTube videos and recording the static video
        information. Video discovery is achieved using any of the standard or category based YouTube video feeds. You can
        configure any video feed combination for the master crawling.
      </p>
      <h2 id="a2">The Slave Crawler</h2>
      <p>
        The <em>slave</em> is used to crawl dynamic video data. Like the master, multiple instances of the slave can be
        used at the same time to load-balance the crawling activity, each slave being assigned a unique identifier.
      </p>
      <h2 id="a3">The Processing Agent</h2>
      <p className="notice">This feature is under development.</p>
      <NavLinks />

      <h1 id="dbcluster">II. Database Clustering</h1>
      <p>This is a new feature that will be made available in release 2.</p>
      <NavLinks />

      <h1 id="usage">III. Using the Crawler</h1>
      <p>
        The master, the slave and the processing agent are implemented within the same software application. Typically,
        you must follow these steps to setup a crawling scenario:
      </p>
      <ul>
        <li><a href="#u1">Configure the connection to the database</a></li>
        <li><a href="#u2">Configure the master and the slave</a></li>
        <li><a href="#u3">Setup the video categories to crawl, if necessary</a></li>
        <li><a href="#u4">Change the crawling order for video feeds, if necessary</a></li>
        <li><a href="#u5">Start the master</a></li>
        <li><a href="#u6">Start the slave</a></li>
        <li><a href="#u7">Inspect your results during or after the crawling</a></li>
      </ul>
      <h2 id="u1">Configure the Connection to the Database</h2>
      <p>
        To configure the connection to the Oracle database, you have to enter the name of the database (see the{' '}
        <a href="#setup">setup section</a> for details), username and password.
      </p>
      <p>
        <img src="/projects/ytcrawler/server.png" width={214} height={167} alt="Configuring the database connection" />
      </p>
      <h2 id="u2">Configure the Master and the Slave</h2>
      <p>Before you start the crawling you must configure the following information:</p>
      <ul>
        <li>General crawler parameters: maximum number videos and the number of slaves</li>
        <li>Database parameters: the name of the database tables you use for crawling</li>
        <li>Video discovery parameters and video feeds</li>
        <li>Master configuration and Slave configuration</li>
      </ul>
      <h2 id="u3">Setup Video Categories</h2>
      <p>Optional, you can configure any selection of video categories (see <a href="#feeds">video feeds</a> for more information).</p>
      <h2 id="u4">Change Video Feeds</h2>
      <p>Optional, you can also modify the order of the video feeds.</p>
      <h2 id="u5">Start the Master</h2>
      <p>Start the master crawler. The master will run periodically with the setup schedule until stopped.</p>
      <h2 id="u6">Start the Slave</h2>
      <p>Start the slave crawler. The slave will run periodically with the setup schedule until stopped.</p>
      <h2 id="u7">View Your Data</h2>
      <p className="notice">This feature may be under future development.</p>
      <p>You can use the YouTube Crawler to analyze your data, either in real time during the crawling, or at the end.</p>
      <NavLinks />

      <h1 id="feeds">IV. Understanding Video Feeds</h1>
      <p>
        For the master crawling, you have to configure video feeds. The YouTube Crawler uses two main types of videos
        feeds: <em>standard</em> feeds and <em>category</em> feeds. Standard feeds are associated with certain classes
        of videos. The following table shows the set of standard feeds currently supported.
      </p>
      <table className="information" style={{ width: '900px' }}>
        <tbody>
          <tr>
            <th className="strong" style={{ width: '250px' }}>Feed</th>
            <th>Information</th>
          </tr>
          {standardFeeds.map((f) => (
            <tr key={f.name}>
              <td className="strong">{f.name}</td>
              <td>
                <strong>URL:</strong> {f.url}
                <br />
                <strong>Description:</strong> {f.desc}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <NavLinks />

      <h1 id="setup">V. Setup Guide</h1>
      <h2>System Requirements</h2>
      <p>To install and run the YouTube Crawler, you need the following:</p>
      <ul>
        <li>Operating system: Microsoft Windows XP SP3 or newer</li>
        <li>Oracle Instant Client for Windows</li>
        <li>Microsoft .NET Framework 3.5</li>
      </ul>
      <p>In addition, you need an Oracle database.</p>
      <h2>Create the Database Tables</h2>
      <p>
        The YouTube Crawler uses two tables, one for <em>static videos</em> data, one for <em>dynamic snapshot</em>{' '}
        data. The master writes information to the <em>videos</em> table, the slave writes information to the{' '}
        <em>snapshot</em> table.
      </p>
      <h2>Install YouTube Crawler</h2>
      <p>
        <a href="#dload">Download</a> and use the setup MSI file to install YouTube Crawler on your computer.
      </p>
      <NavLinks />

      <h1 id="code">VI. Code Information</h1>
      <p>Code information is not yet available for the pre-release version.</p>
      <NavLinks />

      <h1 id="dload">VII. Download Software</h1>
      <p>You can download the software installer for the pre-release version of YouTube Crawler.</p>
      <p>
        <a href="/download/YtCrawlerSetup.msi">Download installer</a> (MSI/2.50 MB)
      </p>
      <p>
        For the pre-release version, the code is not yet available for download. <Link to="/contact">Contact me</Link>, if
        you wish to obtain the open source code from the latest development build.
      </p>
      <NavLinks />
      <p className="update">Last updated: May 26, 2010</p>
    </div>
  );
};

export default YtCrawlerRelease1Page;
